import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Tag } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Chips, Loading, Empty, Modal } from "@/components/atoms";
import Gate from "@/components/Gate";

const CATS = ["All", "Tattoo", "Aftercare", "Merch", "Event"];

function daysLeft(expiry) {
  if (!expiry) return null;
  const d = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  return d;
}

export default function Offers() {
  const [cat, setCat] = useState("All");
  const [offers, setOffers] = useState(null);
  const [active, setActive] = useState(null);
  const [myCode, setMyCode] = useState(null);

  const load = async (c) => {
    setOffers(null);
    try {
      const { data } = await api.get("/offers", { params: c && c !== "All" ? { category: c } : {} });
      setOffers(data);
    } catch (e) {
      setOffers([]);
    }
  };
  useEffect(() => { load(cat); }, [cat]);

  useEffect(() => {
    if (!active) { setMyCode(null); return; }
    setMyCode(null);
    api.get(`/offers/${active.id}/mycode`).then(({ data }) => setMyCode(data)).catch(() => {});
  }, [active]);

  return (
    <Gate>
      <div data-testid="offers-page">
        <div className="px-5 pt-5">
          <span className="eyebrow">Members Perks</span>
          <h1 className="font-display font-black text-3xl uppercase leading-tight">Club Offers</h1>
        </div>
        <Chips options={CATS} value={cat} onChange={setCat} testid="offer-chip" />

        {offers === null ? (
          <Loading />
        ) : offers.length === 0 ? (
          <Empty title="No offers here yet" sub="Check back soon for fresh deals." />
        ) : (
          <div className="px-5 pb-8 flex flex-col gap-4">
            {offers.map((o) => {
              const dl = daysLeft(o.expiry);
              return (
                <button
                  key={o.id}
                  className="atc-card overflow-hidden text-left rise-in"
                  onClick={() => setActive(o)}
                  data-testid={`offer-card-${o.id}`}
                >
                  {o.image_url && <img src={fileUrl(o.image_url)} alt={o.title} className="w-full h-40 object-cover border-b-2 border-black" />}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="font-mono2 text-[10px] uppercase tracking-widest text-[#8B1E28]">{o.category}</span>
                        <h3 className="font-display font-bold text-lg leading-tight">{o.title}</h3>
                      </div>
                      {o.is_event || !o.discount_pct ? (
                        <div className="bg-[#C7A656] text-[#121212] border-2 border-black rounded px-2 py-1 font-display font-black text-xs shrink-0 uppercase">Event</div>
                      ) : (
                        <div className="bg-[#8B1E28] text-[#F4F0E6] border-2 border-black rounded px-2 py-1 font-display font-black text-lg shrink-0">
                          {o.discount_pct}%
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-[#554E40] mt-2 line-clamp-2">{o.description}</p>
                    {dl != null && (
                      <p className="font-mono2 text-xs mt-2 text-[#8B1E28]">{dl > 0 ? `${dl} days left` : "Expired"}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <Modal open={!!active} onClose={() => setActive(null)} title={active?.title || ""} testid="offer-modal">
          {active && (
            <div>
              {active.image_url && <img src={fileUrl(active.image_url)} alt={active.title} className="w-full h-48 object-cover border-2 border-black rounded mb-4" />}
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono2 text-xs uppercase tracking-widest text-[#8B1E28]">{active.category}</span>
                {active.is_event || !active.discount_pct ? (
                  <span className="bg-[#C7A656] text-[#121212] border-2 border-black rounded px-3 py-1 font-display font-black uppercase text-sm">Members Event</span>
                ) : (
                  <span className="bg-[#8B1E28] text-[#F4F0E6] border-2 border-black rounded px-3 py-1 font-display font-black">{active.discount_pct}% OFF</span>
                )}
              </div>
              <p className="text-sm mb-4">{active.description}</p>
              {active.is_event || !active.discount_pct ? (
                <div className="atc-card p-4 bg-[#F4E9C9] text-center" data-testid="offer-event-note">
                  <p className="font-display font-bold uppercase">Members-Only Event</p>
                  <p className="text-sm text-[#554E40] mt-1">No code needed — just show up and rep the club.</p>
                </div>
              ) : (
                <div className="atc-card p-4 flex items-center justify-between bg-[#F9F6F0]">
                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#554E40]">Your Personal Code</p>
                    <p className="font-mono2 font-bold text-xl tracking-wider" data-testid="offer-promo">{myCode ? myCode.code : "…"}</p>
                    {myCode?.redeemed && <p className="font-mono2 text-[10px] text-[#8B1E28]">Already redeemed</p>}
                  </div>
                  <button
                    className="atc-btn btn-gold px-4"
                    disabled={!myCode}
                    onClick={() => { navigator.clipboard?.writeText(myCode.code); toast.success("Code copied"); }}
                    data-testid="offer-copy"
                  >
                    <Copy size={16} /> Copy
                  </button>
                </div>
              )}
              {active.expiry && <p className="font-mono2 text-xs mt-4 text-center text-[#554E40]">Expires {new Date(active.expiry).toLocaleDateString()}</p>}
            </div>
          )}
        </Modal>
      </div>
    </Gate>
  );
}
